import { jsPDF } from 'jspdf'
import { formatDateTime, formatNumber } from './format'

/**
 * Client-side invoice PDF — built from an invoice row on download, the
 * same approach as the app's other exports (no server rendering, no
 * storage). Helvetica is Latin-only, so money is printed as a currency
 * CODE ("INR 499.00"), never the ₹/₹ glyph which would drop out. The
 * number itself follows the person's separators (shared `formatNumber`).
 */

const money = (n, currency = 'INR') =>
  `${currency} ${formatNumber(Number(n ?? 0), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const cadenceLabel = (c) =>
  c === 'yearly' ? 'Yearly' : c === 'two_year' ? '2 Years' : 'Monthly'

export function downloadInvoicePdf(inv, { brandName = 'TaskMagix' } = {}) {
  if (!inv) return
  const doc = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
  const pageW = doc.internal.pageSize.getWidth()
  const margin = 48
  const right = pageW - margin
  const currency = inv.currency || 'INR'
  let y = margin

  // ---- Header ----
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(20)
  doc.setTextColor(13, 44, 92) // brand navy
  doc.text(brandName, margin, y + 4)

  doc.setFontSize(22)
  doc.setTextColor(17, 17, 17)
  doc.text('INVOICE', right, y + 4, { align: 'right' })
  y += 22
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.setTextColor(110, 110, 110)
  doc.text(inv.invoiceNumber || '—', right, y, { align: 'right' })
  y += 14
  doc.text(`Issued ${formatDateTime(inv.issuedAt)}`, right, y, { align: 'right' })

  // Divider.
  y += 18
  doc.setDrawColor(220)
  doc.setLineWidth(0.8)
  doc.line(margin, y, right, y)
  y += 26

  // ---- Bill to ----
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(120, 120, 120)
  doc.text('BILL TO', margin, y)
  y += 15
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(20, 20, 20)
  doc.text(inv.billToName || '—', margin, y)
  if (inv.billToEmail) {
    y += 15
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(90, 90, 90)
    doc.text(inv.billToEmail, margin, y)
  }
  y += 30

  // ---- Line-items table ----
  const colDesc = margin
  const colAmt = right
  doc.setFillColor(245, 247, 250)
  doc.rect(margin, y - 12, right - margin, 22, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(90, 90, 90)
  doc.text('DESCRIPTION', colDesc + 6, y + 2)
  doc.text('AMOUNT', colAmt - 6, y + 2, { align: 'right' })
  y += 26

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.setTextColor(30, 30, 30)
  const planLine = `${inv.planName || inv.planCode || 'Subscription'} — ${cadenceLabel(inv.cadence)} plan`
  doc.text(planLine, colDesc + 6, y)
  const lineAmount = inv.originalAmount != null ? inv.originalAmount : inv.amount
  doc.text(money(lineAmount, currency), colAmt - 6, y, { align: 'right' })
  y += 22

  // Discount line, if a coupon applied.
  if (inv.discountAmount) {
    doc.setTextColor(20, 120, 73)
    const cpn = inv.couponCode ? ` (${inv.couponCode})` : ''
    doc.text(`Discount${cpn}`, colDesc + 6, y)
    doc.text(`- ${money(inv.discountAmount, currency)}`, colAmt - 6, y, { align: 'right' })
    y += 22
  }

  // ---- Totals ----
  y += 4
  doc.setDrawColor(230)
  doc.line(colAmt - 200, y, colAmt, y)
  y += 18
  const totalRow = (label, val, bold) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal')
    doc.setFontSize(bold ? 12 : 10)
    doc.setTextColor(bold ? 17 : 90, bold ? 17 : 90, bold ? 17 : 90)
    doc.text(label, colAmt - 200, y)
    doc.text(val, colAmt - 6, y, { align: 'right' })
    y += bold ? 22 : 18
  }
  totalRow('Subtotal', money(inv.amount, currency))
  totalRow('Tax', money(inv.tax ?? 0, currency))
  totalRow('Total', money(inv.total ?? inv.amount, currency), true)

  // ---- Status pill ----
  y += 6
  const paid = (inv.status || 'paid') === 'paid'
  doc.setFillColor(paid ? 209 : 241, paid ? 250 : 245, paid ? 229 : 249)
  doc.setTextColor(paid ? 5 : 100, paid ? 122 : 100, paid ? 85 : 110)
  doc.roundedRect(colAmt - 70, y - 11, 70, 18, 4, 4, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.text(paid ? 'PAID' : 'VOID', colAmt - 35, y + 1, { align: 'center' })

  // ---- Footer ----
  const footY = doc.internal.pageSize.getHeight() - margin
  doc.setDrawColor(230)
  doc.line(margin, footY - 40, right, footY - 40)
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8.5)
  doc.setTextColor(140, 140, 140)
  const ref = [
    inv.gateway ? `Paid via ${inv.gateway}` : null,
    inv.paymentRef ? `Ref ${inv.paymentRef}` : null,
  ]
    .filter(Boolean)
    .join('  ·  ')
  if (ref) doc.text(ref, margin, footY - 24)
  doc.text(`Thank you for your business. This is a computer-generated invoice from ${brandName}.`, margin, footY - 10)

  doc.save(`${inv.invoiceNumber || 'invoice'}.pdf`)
}
